import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from '../../utils/seo';

const FeesPage: React.FC = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';
  const [activeCategory, setActiveCategory] = useState('registration');

  useSEO({
    title: 'DMV Service Fees Las Vegas Nevada - Transparent Pricing | Speedy DMV',
    description: 'Transparent DMV service fees in Las Vegas Nevada. Our service fee starts at $120–$150 plus Nevada DMV fees. Contact us for an exact quote. Call 702.979.7202.',
    keywords: 'DMV fees Las Vegas, vehicle registration fee Nevada, title transfer cost Las Vegas, DMV service prices Nevada',
    canonical: `${siteUrl}/fees`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Fees', url: '/fees' }])
      ]
    }
  });

  const categories = [
    {
      key: 'registration',
      title: 'Vehicle Registration',
      icon: 'ri-car-line',
      desc: 'New registrations, renewals, and out-of-state transfers.',
      services: [
        { service: 'New Vehicle Registration', nvDmvFee: 'Varies by vehicle', note: 'Based on weight, type & county' },
        { service: 'Registration Renewal', nvDmvFee: 'Varies by vehicle', note: 'Based on weight, type & county' },
        { service: 'Out-of-State Transfer', nvDmvFee: 'Varies by vehicle', note: 'Includes title + registration fees' },
        { service: 'Motorcycle Registration', nvDmvFee: 'Varies by vehicle', note: 'Based on engine size & type' },
        { service: 'Fleet Registration', nvDmvFee: 'Varies', note: 'Custom quote for multiple vehicles' },
      ],
    },
    {
      key: 'titleTransfer',
      title: 'Title Transfer',
      icon: 'ri-file-transfer-line',
      desc: 'Private party, dealer, gift, and inheritance transfers.',
      services: [
        { service: 'Standard Title Transfer', nvDmvFee: '$29.25', note: 'Nevada DMV title fee' },
        { service: 'Title Transfer + Registration', nvDmvFee: '$29.25 + reg fee', note: 'Combined title and registration' },
        { service: 'Gift Transfer', nvDmvFee: '$29.25', note: 'Affidavit of gift required' },
        { service: 'Inheritance Transfer', nvDmvFee: '$29.25', note: 'Probate documentation required' },
      ],
    },
    {
      key: 'duplicateTitle',
      title: 'Duplicate Title',
      icon: 'ri-file-copy-line',
      desc: 'Replace lost, stolen, or damaged vehicle titles.',
      services: [
        { service: 'Duplicate Title (Lost)', nvDmvFee: '$29.25', note: 'Standard replacement' },
        { service: 'Duplicate Title (Stolen)', nvDmvFee: '$29.25', note: 'Police report required' },
        { service: 'Duplicate Title (Damaged)', nvDmvFee: '$29.25', note: 'Bring damaged original' },
        { service: 'Lien Release + Duplicate', nvDmvFee: '$29.25', note: 'Lien release letter required' },
      ],
    },
    {
      key: 'temporaryPermits',
      title: 'Temporary Permits',
      icon: 'ri-time-line',
      desc: '30-day movement permits and trip permits.',
      services: [
        { service: '30-Day Movement Permit', nvDmvFee: '$15.00', note: 'Standard temporary permit' },
        { service: 'Trip Permit (1–3 days)', nvDmvFee: '$15.00', note: 'Short-term interstate travel' },
        { service: 'Emergency Permit', nvDmvFee: '$15.00', note: 'Urgent transportation needs' },
        { service: 'Dealer Permit', nvDmvFee: 'Varies', note: 'Licensed dealers only' },
      ],
    },
    {
      key: 'licensePlates',
      title: 'License Plates',
      icon: 'ri-price-tag-3-line',
      desc: 'Standard, specialty, personalized, and replacement plates.',
      services: [
        { service: 'Standard Plates', nvDmvFee: '$8.00', note: 'Issued same day' },
        { service: 'Personalized Plates', nvDmvFee: '$36.00/yr', note: '2–4 weeks production' },
        { service: 'Specialty Plates', nvDmvFee: 'Varies by design', note: '2–4 weeks production' },
        { service: 'Replacement Plates', nvDmvFee: '$8.00', note: 'Lost, stolen, or damaged' },
      ],
    },
    {
      key: 'notary',
      title: 'Notary Services',
      icon: 'ri-quill-pen-line',
      desc: 'Document notarization for all legal and DMV paperwork.',
      services: [
        { service: 'Standard Notarization', nvDmvFee: 'N/A', note: 'Per signature, state-regulated' },
        { service: 'Vehicle Title Notarization', nvDmvFee: 'N/A', note: 'Included with DMV services' },
        { service: 'Mobile Notary', nvDmvFee: 'N/A', note: 'Travel fee applies' },
        { service: 'Bulk Document Package', nvDmvFee: 'N/A', note: 'Volume discount available' },
      ],
    },
  ];

  const active = categories.find(c => c.key === activeCategory) || categories[0];

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* HERO */}
      <section className="relative pt-[104px]">
        <div className="relative h-[320px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/28b121aacd59b1d838eaeb882517aa08.jpg"
            alt="DMV Service Fees"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#003366]/82"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="flex items-center space-x-2 text-white/60 text-xs mb-4">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#F5A623]">Fees</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
              Service <span className="text-[#F5A623]">Fees</span>
            </h1>
            <p className="text-white/80 text-base max-w-xl">
              Our service fee starts at <strong className="text-[#F5A623]">$120–$150</strong> plus applicable Nevada DMV fees. Contact us for an exact quote.
            </p>
          </div>
        </div>
      </section>

      {/* NOTICE BAR */}
      <div className="bg-[#FFF8E7] border-b-2 border-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0"></i>
            <p className="text-sm text-[#7a5c00]">
              <strong>We are NOT the Nevada DMV.</strong> Speedy DMV Services is a licensed, independent 3rd party provider. We assist with DMV transactions for a service fee. Official DMV: <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold">dmv.nv.gov</a>
            </p>
          </div>
        </div>
      </div>

      {/* PRICING BANNER */}
      <div className="bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#1a4480]">
            <div className="py-5 px-6 text-center">
              <p className="text-[#F5A623] text-xl font-bold">$120 – $150</p>
              <p className="text-white/70 text-xs mt-0.5">Our Service Fee (minimum)</p>
            </div>
            <div className="py-5 px-6 text-center">
              <p className="text-[#F5A623] text-xl font-bold">+ Nevada DMV Fees</p>
              <p className="text-white/70 text-xs mt-0.5">Varies by service & vehicle type</p>
            </div>
            <div className="py-5 px-6 text-center">
              <p className="text-[#F5A623] text-xl font-bold">Call for Exact Quote</p>
              <p className="text-white/70 text-xs mt-0.5">702.979.7202 — No hidden fees</p>
            </div>
          </div>
        </div>
      </div>

      {/* QUOTE CTA */}
      <section className="py-10 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FFF8E7] border border-[#F5A623]/40 rounded-lg p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 flex items-center justify-center bg-[#F5A623] rounded flex-shrink-0">
                <i className="ri-money-dollar-circle-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#003366] text-base mb-1">Every situation is different — get an exact quote</h3>
                <p className="text-sm text-[#7a5c00]">Our minimum service fee is <strong>$120–$150</strong>, plus Nevada DMV fees which vary by vehicle type, weight, county, and service. Call us for a precise total before your visit.</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-6 py-3 rounded font-bold text-sm transition-colors whitespace-nowrap">
                <i className="fas fa-phone text-xs"></i>
                <span>Call for Quote</span>
              </a>
              <Link to="/contact" className="flex items-center justify-center space-x-2 border-2 border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white px-6 py-3 rounded font-bold text-sm transition-colors whitespace-nowrap">
                <i className="ri-mail-line"></i>
                <span>Contact Us</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY TABS */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Nevada DMV Fee Reference</h2>
          <p className="text-gray-500 mb-8 pl-5">Below are the standard Nevada DMV fees for each service. Our service fee of $120–$150 is added on top.</p>

          {/* Tab bar */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map(cat => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex items-center space-x-2 px-4 py-2.5 rounded font-semibold text-sm transition-colors whitespace-nowrap ${activeCategory === cat.key ? 'bg-[#003366] text-white' : 'bg-white border border-gray-200 text-[#333] hover:border-[#003366] hover:text-[#003366]'}`}
              >
                <i className={`${cat.icon} text-sm ${activeCategory === cat.key ? 'text-[#F5A623]' : ''}`}></i>
                <span>{cat.title}</span>
              </button>
            ))}
          </div>

          {/* Fee table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-[#003366] px-6 py-4 flex items-center space-x-3">
              <i className={`${active.icon} text-[#F5A623] text-lg`}></i>
              <div>
                <h3 className="text-white font-bold text-base">{active.title}</h3>
                <p className="text-white/60 text-xs">{active.desc}</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-5 text-xs font-bold text-[#003366] uppercase tracking-widest">Service</th>
                    <th className="text-center py-3 px-5 text-xs font-bold text-[#003366] uppercase tracking-widest">NV DMV Fee</th>
                    <th className="text-center py-3 px-5 text-xs font-bold text-[#003366] uppercase tracking-widest">Our Service Fee</th>
                    <th className="text-center py-3 px-5 text-xs font-bold text-[#003366] uppercase tracking-widest">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {active.services.map((row, i) => (
                    <tr key={i} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                      <td className="py-4 px-5 font-semibold text-[#003366] text-sm">{row.service}</td>
                      <td className="py-4 px-5 text-center text-sm text-gray-700">{row.nvDmvFee}</td>
                      <td className="py-4 px-5 text-center">
                        <span className="inline-flex items-center space-x-1 bg-[#FFF8E7] border border-[#F5A623]/30 text-[#7a5c00] text-xs font-bold px-3 py-1 rounded">
                          <i className="ri-phone-line text-[#F5A623]"></i>
                          <span>Call for Quote</span>
                        </span>
                      </td>
                      <td className="py-4 px-5 text-center text-xs text-gray-400">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-4 bg-[#FFF8E7] border-t border-[#F5A623]/20">
              <p className="text-xs text-[#7a5c00]">
                <strong>Our minimum service fee is $120–$150</strong> per transaction, added to the Nevada DMV fees above. Final total depends on vehicle type, weight, county, and specific transaction details. <a href="tel:7029797202" className="underline font-semibold">Call 702.979.7202</a> for an exact quote before your visit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PAYMENT METHODS */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Accepted Payment Methods</h2>
          <p className="text-gray-500 mb-8 pl-5">We make payment easy and convenient.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { icon: 'ri-money-dollar-circle-line', label: 'Cash' },
              { icon: 'ri-bank-card-line', label: 'Credit / Debit Card' },
              { icon: 'ri-file-text-line', label: 'Personal Check' },
              { icon: 'ri-secure-payment-line', label: "Cashier's Check" },
              { icon: 'ri-mail-line', label: 'Money Order' },
            ].map((p, i) => (
              <div key={i} className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="w-10 h-10 flex items-center justify-center bg-[#003366] rounded flex-shrink-0">
                  <i className={`${p.icon} text-[#F5A623] text-base`}></i>
                </div>
                <span className="font-semibold text-[#003366] text-sm">{p.label}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-4 pl-1">Credit card processing fees may apply. Checks must include valid ID. Receipts provided for all transactions.</p>
        </div>
      </section>

      {/* ADDITIONAL FEES INFO */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Additional Fees That May Apply</h2>
          <p className="text-gray-500 mb-8 pl-5">Depending on your vehicle and situation, these additional fees may be required by the Nevada DMV.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: 'ri-alarm-warning-line', title: 'Late Registration Penalties', desc: 'Fees increase the longer registration is overdue. Call us to calculate your exact penalty.' },
              { icon: 'ri-bank-line', title: 'Lien Recording Fees', desc: 'Required when adding or releasing a lien on a vehicle title.' },
              { icon: 'ri-leaf-line', title: 'Emissions Testing', desc: 'Smog certificate required for most Clark County vehicles. ~$25–$50 at a testing station.' },
              { icon: 'ri-search-line', title: 'VIN Verification', desc: 'Required for out-of-state vehicles. Performed by law enforcement or licensed inspector.' },
              { icon: 'ri-speed-line', title: 'Expedited Processing', desc: 'Rush processing available for urgent situations. Additional fee applies.' },
              { icon: 'ri-percent-line', title: 'Sales Tax', desc: 'Clark County sales tax of 8.375% applies to vehicle purchases. Calculated on sale price.' },
            ].map((item, i) => (
              <div key={i} className="group flex items-start space-x-4 p-5 bg-white border border-gray-200 rounded-lg hover:border-[#003366] transition-all">
                <div className="w-11 h-11 flex items-center justify-center bg-[#003366] rounded flex-shrink-0 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${item.icon} text-[#F5A623] group-hover:text-white text-base transition-colors`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#003366] text-sm mb-1">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            Get Your <span className="text-[#F5A623]">Exact Quote</span>
          </h2>
          <p className="text-white/75 mb-8 leading-relaxed">
            Every transaction is different. Call us and we'll give you a precise total — no surprises, no hidden fees.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="fas fa-phone text-sm"></i>
              <span>Call 702.979.7202</span>
            </a>
            <Link to="/contact" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="ri-mail-line"></i>
              <span>Send a Message</span>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-5">
            {['No Hidden Fees', 'Transparent Pricing', 'Licensed Provider', 'Same-Day Service'].map(b => (
              <div key={b} className="flex items-center space-x-1.5 text-white/60 text-xs">
                <i className="ri-checkbox-circle-fill text-[#F5A623]"></i>
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeesPage;
